import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Modal } from '@bruno/ui';
import type { AgentDTO, CreateAgentInput } from '@bruno/shared';
import { createAgent, deleteAgent, listTeamAgents, updateAgent } from '../api/team';
import { useAuth } from '../auth/AuthContext';

export function TeamPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['team', 'agents'],
    queryFn: listTeamAgents,
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AgentDTO | null>(null);

  const createMut = useMutation({
    mutationFn: createAgent,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'agents'] });
      setOpen(false);
    },
  });
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateAgent>[1] }) =>
      updateAgent(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'agents'] });
      setEditing(null);
    },
  });
  const deleteMut = useMutation({
    mutationFn: deleteAgent,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'agents'] }),
  });

  if (user?.role !== 'owner') {
    return (
      <div className="text-sm text-slate-500">
        Only the account owner can manage team members.
      </div>
    );
  }

  const summary = data;
  const used = summary?.usedAgents ?? 0;
  const max = summary?.maxAgents ?? 0;
  const atLimit = max > 0 && used >= max;
  const featureDisabled = max === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Team</h1>
          <p className="text-sm text-slate-500">
            Add human agents who can take over conversations from Bruno.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={featureDisabled ? 'gray' : atLimit ? 'amber' : 'green'}>
            {used} / {max} agents
          </Badge>
          <Button
            disabled={featureDisabled || atLimit}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            + Add agent
          </Button>
        </div>
      </div>

      {featureDisabled ? (
        <Card>
          <div className="text-sm text-slate-700">
            Your current plan does not include human-agent handoff. Upgrade to a plan that
            allows agents to enable this feature.
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !summary?.agents.length ? (
        <Card className="py-12 text-center">
          <h2 className="text-lg font-semibold mb-1">No agents yet</h2>
          <p className="text-sm text-slate-500">
            Add an agent to start receiving handoffs from Bruno.
          </p>
        </Card>
      ) : (
        <div className="grid gap-2">
          {summary.agents.map((a) => (
            <Card key={a.id}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{a.name ?? a.email}</span>
                    <PresencePill status={a.onlineStatus} />
                    {a.suspended ? <Badge tone="red">suspended</Badge> : null}
                  </div>
                  <div className="text-sm text-slate-500">{a.email}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {a.activeConversations} active conversation
                    {a.activeConversations === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="secondary" onClick={() => setEditing(a)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      patchMut.mutate({
                        id: a.id,
                        body: { suspended: !a.suspended },
                      })
                    }
                  >
                    {a.suspended ? 'Unsuspend' : 'Suspend'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Delete agent ${a.email}? This cannot be undone.`))
                        deleteMut.mutate(a.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateAgentModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(values) => createMut.mutate(values)}
        loading={createMut.isPending}
        error={createMut.isError ? (createMut.error as Error).message : null}
      />
      <EditAgentModal
        agent={editing}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        loading={patchMut.isPending}
      />
    </div>
  );
}

function PresencePill({ status }: { status: 'online' | 'away' | 'offline' }) {
  const tone = status === 'online' ? 'green' : status === 'away' ? 'amber' : 'gray';
  return <Badge tone={tone}>{status}</Badge>;
}

function CreateAgentModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (v: CreateAgentInput) => void;
  loading: boolean;
  error: string | null;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setPassword('');
    }
  }, [open]);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ name: name.trim(), email: email.trim().toLowerCase(), password });
  }

  return (
    <Modal open={open} onClose={onClose} title="Add agent">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="off"
        />
        <Input
          label="Temporary password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          required
          autoComplete="new-password"
        />
        {error ? <div className="text-sm text-red-600">{error}</div> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Add agent
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditAgentModal({
  agent,
  onClose,
  onSubmit,
  loading,
}: {
  agent: AgentDTO | null;
  onClose: () => void;
  onSubmit: (body: { name?: string }) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  useEffect(() => {
    setName(agent?.name ?? '');
  }, [agent]);
  if (!agent) return null;
  return (
    <Modal open={!!agent} onClose={onClose} title={`Edit ${agent.email}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const body: { name?: string } = {};
          if (name.trim() && name !== agent.name) body.name = name.trim();
          onSubmit(body);
        }}
        className="flex flex-col gap-3"
      >
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <p className="text-xs text-slate-500">
          Agents change their own password via the &quot;Forgot password?&quot; link on the
          sign-in page or under Account settings.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
